// Global keyboard shortcuts — port of the legacy `app.js` keymap (v-hotkey).
//
//   alt+shift+<key>   open the "add" form of a module      (→ `<page>?create=1`)
//   ctrl+shift+<key>  open the list of a module
//   alt+meta+<key>    add (reference / admin modules)
//   ctrl+meta+<key>   list (reference / admin modules)
//   ctrl+alt+a        alerts       ctrl+shift+k  shortcut help
//   ctrl+alt+x        fullscreen   mod+k         command palette
//
// Keys are matched on `KeyboardEvent.code` so macOS option-combos (which change
// `event.key`) still work.

import { useEffect } from 'react';

import { t } from '../../i18n';

export type ShortcutCombo = 'alt+shift' | 'ctrl+shift' | 'alt+meta' | 'ctrl+meta' | 'ctrl+alt';

export type ShortcutTarget = {
  combo: ShortcutCombo;
  key: string; // single character, e.g. 's', ','
  to?: string; // route (no leading slash)
  action?: 'alerts' | 'help' | 'fullscreen' | 'pos';
  labelKey: string; // i18n key of the target
  create?: boolean;
};

// [key, page route, nav label key]
const ADD_LIST: Array<[string, string, string]> = [
  ['s', 'sales', 'nav.sales'],
  ['n', 'incomes', 'nav.incomes'],
  ['a', 'payments', 'nav.payments'],
  ['e', 'expenses', 'nav.expenses'],
  ['c', 'customers', 'nav.customers'],
  ['v', 'suppliers', 'nav.suppliers'],
  ['m', 'modifiers', 'nav.modifiers'],
  ['b', 'purchases', 'nav.purchases'],
  ['d', 'recurring-sales', 'nav.recurringSales'],
  ['g', 'gift-cards', 'nav.giftCards'],
  ['q', 'quotations', 'nav.quotations'],
  ['r', 'return-orders', 'nav.returns'],
  ['t', 'stock-transfers', 'nav.stockTransfers'],
  ['i', 'items', 'nav.products'],
  ['z', 'combos', 'nav.combos'],
  ['x', 'recipes', 'nav.recipes'],
  ['y', 'services', 'nav.services']
];

const META_ADD_LIST: Array<[string, string, string]> = [
  ['h', 'halls', 'nav.halls'],
  ['t', 'taxes', 'nav.taxes'],
  ['x', 'units', 'nav.units'],
  ['u', 'users', 'nav.users'],
  ['b', 'brands', 'nav.brands'],
  ['f', 'fields', 'nav.fields'],
  ['p', 'promotions', 'nav.promotions'],
  ['l', 'locations', 'nav.locations'],
  ['c', 'categories', 'nav.categories'],
  ['g', 'customer-groups', 'nav.customerGroups'],
  ['z', 'asset-transfers', 'nav.assetTransfers']
];

export const SHORTCUTS: ShortcutTarget[] = [
  ...ADD_LIST.map(([key, to, labelKey]) => ({ combo: 'alt+shift' as const, key, to: `${to}?create=1`, labelKey, create: true })),
  ...ADD_LIST.map(([key, to, labelKey]) => ({ combo: 'ctrl+shift' as const, key, to, labelKey })),
  { combo: 'ctrl+shift', key: 'p', action: 'pos', labelKey: 'nav.pos' },
  { combo: 'ctrl+shift', key: 'h', to: '', labelKey: 'nav.dashboard' },
  { combo: 'ctrl+shift', key: 'o', to: 'orders', labelKey: 'nav.orders' },
  ...META_ADD_LIST.map(([key, to, labelKey]) => ({ combo: 'alt+meta' as const, key, to: `${to}?create=1`, labelKey, create: true })),
  ...META_ADD_LIST.map(([key, to, labelKey]) => ({ combo: 'ctrl+meta' as const, key, to, labelKey })),
  { combo: 'ctrl+meta', key: 'r', to: 'reports', labelKey: 'nav.reports' },
  { combo: 'ctrl+meta', key: ',', to: 'settings', labelKey: 'nav.appSettings' },
  { combo: 'ctrl+meta', key: 'y', to: 'reports/activity-logs', labelKey: 'shell.activityLogs' },
  { combo: 'ctrl+alt', key: 'a', action: 'alerts', labelKey: 'shell.alerts' },
  { combo: 'ctrl+alt', key: 'x', action: 'fullscreen', labelKey: 'shell.fullscreen' },
  { combo: 'ctrl+shift', key: 'k', action: 'help', labelKey: 'shell.shortcuts' }
];

export function shortcutLabel(shortcut: ShortcutTarget) {
  const name = t(shortcut.labelKey);
  return shortcut.create ? t('shell.addX', { x: name }) : name;
}

export function comboText(shortcut: Pick<ShortcutTarget, 'combo' | 'key'>) {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  const names: Record<string, string> = { ctrl: isMac ? '⌃' : 'Ctrl', alt: isMac ? '⌥' : 'Alt', shift: isMac ? '⇧' : 'Shift', meta: isMac ? '⌘' : 'Win' };
  return [...shortcut.combo.split('+').map((part) => names[part] ?? part), shortcut.key.toUpperCase()].join(isMac ? '' : '+');
}

function eventKey(event: KeyboardEvent) {
  if (event.code?.startsWith('Key')) return event.code.slice(3).toLowerCase();
  if (event.code === 'Comma') return ',';
  return event.key?.toLowerCase() ?? '';
}

function eventCombo(event: KeyboardEvent): ShortcutCombo | null {
  const { ctrlKey: c, altKey: a, shiftKey: s, metaKey: m } = event;
  if (a && s && !c && !m) return 'alt+shift';
  if (c && s && !a && !m) return 'ctrl+shift';
  if (a && m && !c && !s) return 'alt+meta';
  if (c && m && !a && !s) return 'ctrl+meta';
  if (c && a && !s && !m) return 'ctrl+alt';
  return null;
}

export function matchShortcut(event: KeyboardEvent): ShortcutTarget | null {
  const combo = eventCombo(event);
  if (!combo) return null;
  const key = eventKey(event);
  return SHORTCUTS.find((s) => s.combo === combo && s.key === key) ?? null;
}

/** Is this the command palette chord (⌘K / Ctrl+K)? */
export function isPaletteChord(event: KeyboardEvent) {
  return (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && eventKey(event) === 'k';
}

export function useGlobalShortcuts(handler: (shortcut: ShortcutTarget | 'palette') => void) {
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (isPaletteChord(event)) {
        event.preventDefault();
        handler('palette');
        return;
      }
      const shortcut = matchShortcut(event);
      if (shortcut) {
        event.preventDefault();
        handler(shortcut);
      }
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [handler]);
}

/** Toggle browser fullscreen (legacy FullscreenComponent). */
export function toggleFullscreen() {
  if (document.fullscreenElement) void document.exitFullscreen?.();
  else void document.documentElement.requestFullscreen?.().catch(() => undefined);
}
